import { useEffect, useRef } from 'react';
import { Modal, Status, toast } from '@insight-health-ai/roseate/components';
import { AmbientAi } from '@insight-health-ai/roseate/icons';
import { useTheme } from '@insight-health-ai/roseate/theme';
import {
  DatePicker,
  Form,
  Input,
  Radio,
  Select,
  Switch,
  TimePicker,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useGetPatientDetails } from 'src/hooks/patient';
import {
  PatientDropdown,
  PractitionerDropdown,
  PhoneInput,
} from 'src/components';
import {
  useGetAppointmentConditionList,
  useGetAppointmentDetailsAlternate,
} from 'src/hooks/appointment';
import {
  useGetAppointmentTypes,
  useCreateAppointment,
  useUpdateAppointment,
} from 'src/hooks/appointment';
import {
  Appointment,
  AppointmentType,
  Condition,
  Practitioner,
} from 'src/models';
import {
  customFormValidationRules,
  formatPhoneNumber,
  parsePhoneNumber,
  VALID_PHONE_REGEX,
} from 'src/utils';
import { useAuth } from '@frontegg/react';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { useGetPractitionerNotificationPreferences } from 'src/hooks/practitioner';
import { analytics, logger } from 'src/services';

type FormValues = {
  appointmentId: Appointment['appointment_id'];
  appointmentTypeId: number;
  practitionerId: Practitioner['practitioner_id'];
  conditionId: Condition['id'];
  appointmentDate: Dayjs;
  appointmentTime: Dayjs;
  patientFirstName: string;
  patientLastName: string;
  patientPreferredName: string;
  dateOfBirth: Dayjs;
  email: string;
  phone: string;
  preferredLanguage: string;
  sendReminder: boolean;
};

const DEFAULT_APPOINTMENT_TIME = dayjs().hour(8).minute(0).second(0);

const preferredLanguageOptions = [
  { label: 'English', value: 'en-US' },
  { label: 'Spanish', value: 'es-ES' },
];

export type CreateAppointmentModalProps = {
  open: boolean;
  onClose: () => void;
  initialValues: Partial<FormValues>;
  editAppointmentUuid?: Appointment['appt_uuid'];
};

export const CreateAppointmentModal = ({
  open,
  onClose,
  initialValues,
  editAppointmentUuid,
}: CreateAppointmentModalProps) => {
  const isEditMode = !!editAppointmentUuid;
  const { user } = useAuth();
  const { data: currentPractitioner } = useGetCurrentPractitioner();
  const [patientId, setPatientId] = useState<number | undefined>(undefined);
  const { theme } = useTheme();
  const { data: appointmentTypesData, isLoading: appointmentTypesLoading } =
    useGetAppointmentTypes({}, { enabled: open });

  const [form] = Form.useForm<FormValues>();
  const values = Form.useWatch([], form);

  const { appointmentTypeId, practitionerId, conditionId } = values || {};

  const {
    data: notificationPreferencesData,
    isLoading: isLoadingPractitionerNotificationPreferences,
  } = useGetPractitionerNotificationPreferences(
    {
      practitionerId: practitionerId,
      conditionId: conditionId,
    },
    { enabled: open && !!practitionerId && !!conditionId }
  );

  const { data: conditions, isLoading: conditionsLoading } =
    useGetAppointmentConditionList(
      {
        practitioner_id: practitionerId,
        appt_creation: true,
        appointment_type_id: appointmentTypeId,
      },
      {
        enabled: open && !!practitionerId && !!appointmentTypeId,
      }
    );

  const { data: patientDetails } = useGetPatientDetails(
    {
      patientId: patientId!,
    },
    {
      enabled: open && !!patientId,
    }
  );

  const {
    data: editAppointmentDetails,
    isLoading: isLoadingEditAppointmentDetails,
  } = useGetAppointmentDetailsAlternate(
    {
      appt_uuid: editAppointmentUuid!,
    },
    {
      enabled: open && !!editAppointmentUuid,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const { mutateAsync: createAppointment } = useCreateAppointment();
  const { mutateAsync: updateAppointment } = useUpdateAppointment();

  const handleSubmit = async () => {
    const values = await form.validateFields();

    try {
      const {
        appointmentTypeId,
        practitionerId,
        conditionId,
        appointmentDate,
        appointmentTime,
        patientFirstName,
        patientLastName,
        patientPreferredName,
        dateOfBirth,
        email,
        phone,
        sendReminder,
        preferredLanguage,
      } = values;
      const time = appointmentTime || DEFAULT_APPOINTMENT_TIME;
      const startDateTime = dayjs(appointmentDate)
        .hour(time.hour())
        .minute(time.minute())
        .second(0);
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const finalAppointmentTime = isEditMode
        ? startDateTime.toISOString()
        : startDateTime.format('YYYY-MM-DD HH:mm:ss');

      const isIntakeAppointment = appointmentTypeId === 1;
      const isFollowupWithExistingPatient = !isIntakeAppointment && !!patientId;

      const payload: {
        appointment: Record<string, unknown>;
        patient: Record<string, unknown>;
        notification: Record<string, unknown>;
      } = {
        appointment: {
          ...(isEditMode
            ? { appt_uuid: editAppointmentUuid!, last_modified_by: user?.email }
            : {
                appointment_type: appointmentTypeId || 1,
                status_id: 3, // Booked
                org_id: currentPractitioner?.org_id,
                send_now: !!sendReminder,
              }),
          start_date: finalAppointmentTime,
          time_zone: browserTimezone,
          practitioner_id: practitionerId,
          condition_id: conditionId || null,
          created_by: user?.email,
        },
        patient: {
          birth_date: dateOfBirth?.format('YYYY-MM-DD'),
          first_name: patientFirstName || '',
          last_name: patientLastName,
          preferred_name: patientPreferredName,
          is_newpatient: isIntakeAppointment
            ? true
            : !isFollowupWithExistingPatient,
          address: {
            line_1: '',
            line_2: '',
            country: '',
            zip: '',
            state: '',
            city: '',
          },
          phone: {
            phone_text: parsePhoneNumber(phone),
          },
          email: { email_text: email },
          preferred_language:
            preferredLanguage || preferredLanguageOptions[0].value,
        },
        notification: {
          schedule:
            notificationPreferencesData?.preferences?.notification_preference?.map(
              (pref, index) => ({
                duration: pref.duration,
                number_of: pref.number_of,
                preference: index + 1,
              })
            ) || [],
          emails:
            notificationPreferencesData?.preferences?.emails?.filter(Boolean) ||
            [],
        },
      };

      if (isFollowupWithExistingPatient) {
        payload.patient.existing_followup_patientid = patientId;
      }

      if (isEditMode) {
        await updateAppointment({
          appt_uuid: editAppointmentUuid!,
          ...payload,
        });
        toast.success('Appointment updated successfully');
      } else {
        await createAppointment(payload);
        analytics.track('Created New Appointment', {
          appointment_date: finalAppointmentTime,
          appointment_status: 'Booked',
          practitioner_id: practitionerId,
          email: user?.email,
        });
        toast.success('Appointment created successfully');
      }
      onClose();
    } catch (error) {
      logger.error('Failed to save appointment', {}, error as Error);
      toast.error('Failed to save appointment');
    }
  };

  useEffect(() => {
    if (patientDetails && patientId) {
      const { first_name, last_name, birth_date, email, phone } =
        patientDetails;
      form.setFieldsValue({
        patientFirstName: first_name,
        patientLastName: last_name,
        dateOfBirth: birth_date ? dayjs(birth_date) : undefined,
        email: email?.email_text || '',
        phone: phone?.phone_text ? formatPhoneNumber(phone.phone_text) : '',
      });
    }
  }, [patientDetails]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }
    const fieldsToReset: Partial<FormValues> = {
      conditionId: undefined,
    };
    form.setFieldsValue(fieldsToReset);
  }, [practitionerId, appointmentTypeId]);

  const hasPrefilledValuesRef = useRef(false);
  useEffect(() => {
    if (editAppointmentDetails && !hasPrefilledValuesRef.current) {
      hasPrefilledValuesRef.current = true;
      const {
        practitioner_id,
        appointment_type_id,
        condition_id,
        start_date,
        patient_details,
      } = editAppointmentDetails;

      form.setFieldsValue({
        appointmentTypeId: appointment_type_id,
        appointmentDate: dayjs(start_date),
        appointmentTime: dayjs(start_date),
        patientFirstName: patient_details?.patient_firstName,
        patientLastName: patient_details?.patient_lastName,
        dateOfBirth: patient_details.birth_date
          ? dayjs(patient_details.birth_date)
          : undefined,
        phone: patient_details.phone_text
          ? formatPhoneNumber(patient_details.phone_text)
          : '',
        email: patient_details.email_text || '',
        patientPreferredName: patient_details.preferred_name || '',
        preferredLanguage:
          patient_details.preferred_language ||
          preferredLanguageOptions[0].value,
        practitionerId: practitioner_id,
        conditionId: condition_id,
      });
    }
  }, [editAppointmentDetails]);

  const appointmentTypes: Record<
    AppointmentType['appointment_type_text'],
    { label?: string; style: React.CSSProperties }
  > = {
    Intake: {
      style: { backgroundColor: theme.colors.success[500], color: '#fff' },
    },
    Followup: {
      style: { backgroundColor: theme.colors.info[500], color: '#fff' },
    },
    'Aura encounter': {
      style: { color: theme.colors.primary[700] },
    },
    'Non-VCA': {
      style: { color: '#018749' },
    },
  };

  const selectedAppointmentType = appointmentTypesData?.appointment_types?.find(
    (type) => type.appointment_type_id === appointmentTypeId
  );

  const selectedAppointmentTypeText =
    selectedAppointmentType?.appointment_type_text;

  return (
    <Modal
      open={open}
      loading={appointmentTypesLoading || isLoadingEditAppointmentDetails}
      onClose={onClose}
      size="lg"
      title="Add Appointment"
      primaryAction={{
        label: 'Submit',
        disabled:
          isLoadingPractitionerNotificationPreferences ||
          conditionsLoading ||
          appointmentTypesLoading,
        onClick: handleSubmit,
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          appointmentTime: DEFAULT_APPOINTMENT_TIME,
          ...initialValues,
        }}
      >
        <Form.Item
          rules={[
            customFormValidationRules.requiredWithZeroAllowed({
              message: 'Appointment type is required',
            }),
          ]}
          layout="horizontal"
          name="appointmentTypeId"
          label="Appointment Type"
        >
          <Radio.Group disabled={isEditMode}>
            {appointmentTypesData?.appointment_types
              ?.filter((type) => type.appointment_type_text !== 'Non-VCA')
              .map((type) => {
                const appointmentTypeConfig =
                  appointmentTypes[
                    type.appointment_type_text as keyof typeof appointmentTypes
                  ];
                return (
                  <Radio
                    key={type.appointment_type_id}
                    value={type.appointment_type_id}
                  >
                    <Status
                      icon={
                        type.appointment_type_text === 'Aura encounter' ? (
                          <AmbientAi />
                        ) : undefined
                      }
                      size="sm"
                      label={type.appointment_type_text}
                      {...appointmentTypeConfig}
                    />
                  </Radio>
                );
              })}
          </Radio.Group>
        </Form.Item>
        <div className="grid grid-cols-3 gap-4">
          <Form.Item
            className="field-wrapper"
            name="practitionerId"
            label="Practitioner"
            rules={[
              customFormValidationRules.requiredWithZeroAllowed({
                message: 'Practitioner is required',
              }),
            ]}
          >
            <PractitionerDropdown className="field" disabled={isEditMode} />
          </Form.Item>
          {!!practitionerId &&
            selectedAppointmentTypeText !== 'Aura encounter' && (
              <Form.Item
                className="field-wrapper"
                name="conditionId"
                label="Condition"
                rules={[
                  customFormValidationRules.requiredWithZeroAllowed({
                    message: 'Condition is required',
                  }),
                ]}
              >
                <Select
                  className="field"
                  placeholder="Select a condition"
                  loading={conditionsLoading}
                  disabled={conditionsLoading || isEditMode}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={conditions?.conditions?.map((condition) => ({
                    label: condition.condition_name,
                    value: condition.condition_id,
                  }))}
                />
              </Form.Item>
            )}
          <div className="">
            <div className="mb-2">
              <label>Appointment Date & Time</label>
            </div>
            <div className="flex gap-1">
              <Form.Item
                className="mb-0 flex-1 field-wrapper"
                name="appointmentDate"
                rules={[
                  customFormValidationRules.required({
                    message: 'Date is required',
                  }),
                ]}
              >
                <DatePicker
                  disabledDate={(date) => date && date < dayjs().startOf('day')}
                  className="w-full field"
                  placeholder="Select date"
                  format={{ format: 'MM/DD/YYYY', type: 'mask' }}
                />
              </Form.Item>
              <Form.Item
                className="mb-0 w-[96px] field-wrapper"
                name="appointmentTime"
                rules={[
                  customFormValidationRules.required({
                    message: 'Time is required',
                  }),
                ]}
              >
                <TimePicker
                  suffixIcon={null}
                  allowClear={false}
                  className="w-full field"
                  format="h:mm A"
                  use12Hours
                  placeholder="Time"
                />
              </Form.Item>
            </div>
          </div>
        </div>
        {selectedAppointmentTypeText === 'Followup' && (
          <>
            <div className="font-medium text-lg mt-4 mb-2">Search Patient</div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-2 items-center gap-2">
              <div className="field-wrapper pb-2">
                {/* @ts-ignore */}
                <PatientDropdown
                  selectProps={{
                    className: 'field w-320',
                  }}
                  onChange={(patientId: number) => {
                    setPatientId(patientId);
                  }}
                />
              </div>
              Or Add Patient Details
            </div>
          </>
        )}
        <div className="font-medium text-lg mt-4 mb-2">Add Patient Details</div>
        <div className="grid grid-cols-3 gap-x-4 gap-y-2">
          <Form.Item
            className="field-wrapper"
            name="patientFirstName"
            label="First Name"
            rules={[
              customFormValidationRules.required({
                message: 'First name is required',
              }),
              customFormValidationRules.noLeadingOrTrailingSpaces({
                message: 'First name cannot have leading or trailing spaces',
              }),
            ]}
          >
            <Input className="field" placeholder="Enter first name" />
          </Form.Item>
          <Form.Item
            className="field-wrapper"
            name="patientLastName"
            label="Last Name"
            rules={[
              customFormValidationRules.required({
                message: 'Last name is required',
              }),
              customFormValidationRules.noLeadingOrTrailingSpaces({
                message: 'Last name cannot have leading or trailing spaces',
              }),
            ]}
          >
            <Input className="field" placeholder="Enter last name" />
          </Form.Item>
          {selectedAppointmentTypeText !== 'Aura encounter' && (
            <Form.Item
              className="field-wrapper"
              name="patientPreferredName"
              label="Preferred Name"
              rules={[
                customFormValidationRules.noLeadingOrTrailingSpaces({
                  message:
                    'Preferred name cannot have leading or trailing spaces',
                }),
              ]}
            >
              <Input className="field" placeholder="Enter preferred name" />
            </Form.Item>
          )}
          <Form.Item
            className="field-wrapper"
            name="dateOfBirth"
            label="Date of Birth"
            rules={[
              customFormValidationRules.required({
                message: 'Date of birth is required',
              }),
            ]}
          >
            <DatePicker
              disabledDate={(date) => date && date > dayjs().endOf('day')}
              className="field"
              placeholder="Select birth date"
              format={{ format: 'MM/DD/YYYY', type: 'mask' }}
            />
          </Form.Item>
          {selectedAppointmentTypeText !== 'Aura encounter' && (
            <Form.Item
              className="field-wrapper"
              name="phone"
              label="Mobile Number"
              rules={[
                customFormValidationRules.required({
                  message: 'Mobile number is required',
                }),
                {
                  pattern: VALID_PHONE_REGEX,
                  message: 'Invalid phone number',
                },
              ]}
            >
              <PhoneInput className="field" placeholder="Enter mobile number" />
            </Form.Item>
          )}
          {selectedAppointmentTypeText !== 'Aura encounter' && (
            <Form.Item
              className="field-wrapper"
              name="email"
              label="Email"
              rules={[
                customFormValidationRules.required({
                  message: 'Email is required',
                }),
                { type: 'email', message: 'Invalid email address' },
              ]}
            >
              <Input className="field" placeholder="Enter email" />
            </Form.Item>
          )}
          {currentPractitioner?.org_settings?.enable_language_selection && (
            <Form.Item
              className="field-wrapper"
              name="preferredLanguage"
              label="Preferred Language"
              rules={[
                customFormValidationRules.required({
                  message: 'Preferred language is required',
                }),
              ]}
            >
              <Select
                className="field"
                placeholder="Select preferred language"
                options={preferredLanguageOptions}
              />
            </Form.Item>
          )}
        </div>
        {selectedAppointmentTypeText !== 'Aura encounter' && !isEditMode && (
          <Form.Item
            className="mt-3"
            layout="horizontal"
            name="sendReminder"
            valuePropName="checked"
            label="Send a patient reminder immediately"
          >
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
